// =============================================================================
// src/lib/password-strength.ts
//
// Lightweight password-strength scoring. Returns a label + score 0-4 so
// the SignUp / Reset password forms can render a strength meter without
// pulling in a heavy zxcvbn-style dependency.
//
// Scoring is intentionally simple — length matters most, character-class
// variety adds bonus. Anyone serious about password quality should run
// HIBP's pwnedpasswords range check, but that's a network call and
// belongs in a server-side hook, not in the form on first keystroke.
// =============================================================================

export type StrengthLabel = 'too-short' | 'weak' | 'fair' | 'good' | 'strong'

export interface PasswordStrength {
  score: 0 | 1 | 2 | 3 | 4
  label: StrengthLabel
  /** True if the password is long enough to be acceptable (≥ MIN_LENGTH). */
  acceptable: boolean
}

export const MIN_LENGTH = 8

export function checkPasswordStrength(password: string): PasswordStrength {
  if (password.length === 0) {
    return { score: 0, label: 'too-short', acceptable: false }
  }
  if (password.length < MIN_LENGTH) {
    return { score: 0, label: 'too-short', acceptable: false }
  }

  let bonus = 0
  if (password.length >= 12) bonus += 1
  if (password.length >= 16) bonus += 1
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) bonus += 1
  if (/[0-9]/.test(password)) bonus += 1
  if (/[^a-zA-Z0-9]/.test(password)) bonus += 1

  // Map bonus 0..5 to score 1..4.
  let score: PasswordStrength['score']
  let label: StrengthLabel
  if (bonus <= 1) {
    score = 1
    label = 'weak'
  } else if (bonus === 2) {
    score = 2
    label = 'fair'
  } else if (bonus === 3) {
    score = 3
    label = 'good'
  } else {
    score = 4
    label = 'strong'
  }
  return { score, label, acceptable: true }
}
